import * as pushStyles from '../../styles/actualites/push-event.module.scss';

import {GatsbyImage, getImage} from 'gatsby-plugin-image';
import PropTypes from 'prop-types';
import CategoriesContainer from './CategoriesContainer';
import IconContainer from './IconContainer';
import PolyLink from './PolyLink';
import React from 'react';
import {assign} from 'lodash';
import clsx from 'clsx';
import {getI18n, useTranslation} from 'react-i18next';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCircle} from '@fortawesome/free-solid-svg-icons';
import {altNN} from '../../utils/parsing';

const getPreview = (preview) => {
  const previewSize = 1000;
  if (preview && preview.length > previewSize) {
    return preview.substring(0, previewSize) + ' ...';
  }
  return preview;
};

const EventOrNewsList = ({
  preview,
  date,
  end_date,
  live,
  campus,
  isEvent,
  href,
  title,
  cover,
  categories,
  getCategories,
  locale,
  objectID,
}) => {
  const {language} = getI18n();
  const cleanupCampusName = (cn) => {
    if (cn) {
      if (cn.startsWith('LE_') && language === 'fr') {
        return cn.replace('LE_', 'DU ');
      } else if (cn.startsWith('LE_') && language === 'en') {
        return cn.replace('_', ' ');
      } else if (language === 'fr') {
        return `DE ${cn}`;
      }
      return cn.replace('_', ' ');
    }
    return cn;
  };

  const {t} = useTranslation();

  let parsedDate = date ? new Date(date).toLocaleDateString(locale, {
    day: '2-digit', month: '2-digit', year: 'numeric', timeZone:'CET'
  }) : null;

  if (isEvent) {

    const endParsedDate = new Date(end_date).toLocaleDateString(locale, {
      day: '2-digit', month: '2-digit', year: 'numeric', timeZone:'CET'
    });

    if (parsedDate === endParsedDate) {

      parsedDate = t('event.guide.page.date.day', {
        day: new Date(date).toLocaleString(locale, {day: '2-digit'}),
        month: new Date(date).toLocaleString(locale, {month: 'long'}),
        year: new Date(date).toLocaleString(locale, {year: 'numeric'}),
        hour_start: new Date(date).toLocaleString(locale, {hour: '2-digit', minute: '2-digit'}),
        hour_end: new Date(end_date).toLocaleString(locale, {hour: '2-digit', minute: '2-digit'}),
      });

    } else {

      parsedDate = t('event.guide.page.date.MulDay', {
        day_start: new Date(date).toLocaleString(locale, {day: '2-digit'}),
        month_start: new Date(date).toLocaleString(locale, {month: 'long'}),
        year_start: new Date(date).toLocaleString(locale, {year: 'numeric'}),
        hour_start: new Date(date).toLocaleString(locale, {hour: '2-digit', minute: '2-digit'}),
        day_end: new Date(end_date).toLocaleString(locale, {day: '2-digit'}),
        month_end: new Date(end_date).toLocaleString(locale, {month: 'long'}),
        year_end: new Date(end_date).toLocaleString(locale, {year: 'numeric'}),
        hour_end: new Date(end_date).toLocaleString(locale, {hour: '2-digit', minute: '2-digit'}),
      });

    }
  }

  const fullCategories = React.useMemo(() =>
    categories?.map((category) => assign(category, {variant: 'contained'})), [categories]);

  return (
    <PolyLink
      ariaLabel={title}
      className={pushStyles.linkRoot}
      key={objectID}
      name="news-focus"
      url={href}
    >
      <div className={pushStyles.rootSmartphone}>
        <div className={pushStyles.smartphoneImgBloc}>
          <GatsbyImage
            alt={altNN('')}
            image={getImage(cover?.imageFile)}
            loading="eager"
            style={{height: '100%', width: '100%'}}
            title={cover?.name || ''}
          >
          </GatsbyImage>
        </div>
        <div className={pushStyles.descriptionBlocSmartphone}>
          <div
            className={clsx(pushStyles.smartphoneTitleBloc, pushStyles.categoriesSmartphone)}
          >
            {parsedDate && !isEvent && (
              <CategoriesContainer
                elements={[
                  {
                    id: 1, label: parsedDate, color: 'var(--red-color)',
                  },
                ]}
              />
            )}
            {parsedDate && isEvent && (
              <CategoriesContainer
                elements={[
                  {
                    id: 2, label: parsedDate, color: 'var(--red-color)', variant: 'contained', clickOff: true
                  },
                ]}
              />
            )}
            {campus && isEvent && (
              <CategoriesContainer
                elements={[
                  {
                    id: 2,
                    label: t('event.guide.page.campus', {'campus': cleanupCampusName(campus)}),
                    color: 'var(--font-color)',
                    basecolor: 'var(--black-color)',
                    clickOff: true
                  },
                ]}
              />
            )}
            {live && isEvent && (
              <CategoriesContainer
                elements={[
                  {
                    id: 2, label:
                    <>
                      <FontAwesomeIcon
                        aria-hidden="true"
                        focusable="false"
                        icon={faCircle}
                        size="xs"
                        style={{marginRight: 5}}
                      >
                      </FontAwesomeIcon>
                      {'LIVE'}
                    </>, color: 'var(--red-color)', variant: 'contained'
                  },
                ]}
              />)}
            <div className={pushStyles.categorieBloc}>
              {categories?.length > 0 && (
                <CategoriesContainer
                  elements={fullCategories}
                  getCategories={getCategories}
                  isUnique
                />
              )}
            </div>
          </div>
          <div className={pushStyles.smartphoneTitleBloc}>
            <h3 className={pushStyles.title}>{title}</h3>
          </div>
          <div className={pushStyles.smartphonePreviewBloc}>
            <p className={pushStyles.preview}>{getPreview(preview)}</p>
          </div>
          <div className={pushStyles.linkBloc}>
            <p className={pushStyles.more}>{t('event.guide.page.more.url')}</p>
            <div style={{marginLeft: 8}}>
              <IconContainer
                classname={pushStyles.more}
                color="BLACK"
                icon="ARROW_RIGHT_NORMAL"
                size={20}
              />
            </div>
          </div>
        </div>
      </div>
      <div className={pushStyles.rootDesktop}>
        <div className={pushStyles.dateBloc}>
          <GatsbyImage
            alt={altNN('')}
            id={cover?.name || ''}
            image={getImage(cover?.imageFile)}
            loading="eager"
            role={'none'}
            style={{height: '100%', width: '100%'}}
          >
          </GatsbyImage>
        </div>
        <div
          className={clsx(pushStyles.descriptionBlocDesktop, pushStyles.height)}
        >
          <div className={pushStyles.smartphoneTitleBloc}>
            {parsedDate && !isEvent && (
              <CategoriesContainer
                elements={[
                  {
                    id: 1, label: parsedDate, color: 'var(--red-color)',
                  },
                ]}
              />
            )}
            {parsedDate && isEvent && (
              <CategoriesContainer
                elements={[
                  {
                    id: 2, label: parsedDate, color: 'var(--red-color)', variant: 'contained', clickOff: true
                  },
                ]}
              />
            )}
            {campus && isEvent && (
              <CategoriesContainer
                elements={[
                  {
                    id: 42,
                    label: t('event.guide.page.campus', {'campus': cleanupCampusName(campus)}),
                    color: 'var(--font-color)',
                    basecolor: 'var(--black-color)',
                    clickOff: true
                  },
                ]}
              />
            )}
            {live && isEvent && (
              <CategoriesContainer
                elements={[
                  {
                    id: 42, label:
                    <>
                      <FontAwesomeIcon
                        aria-hidden="true"
                        focusable="false"
                        icon={faCircle}
                        size="xs"
                        style={{marginRight: 5, color: 'var(--red-color)'}}
                      />
                      {'LIVE'}
                    </>, color: 'var(--red-color)', clickOff: true,
                  },
                ]}
              />)}
            {categories?.length > 0 && (
              <CategoriesContainer
                elements={fullCategories}
                getCategories={getCategories}
                isUnique
              />
            )}
          </div>
          <div className={pushStyles.titleBloc}>
            <h3 className={pushStyles.title}>{title}</h3>
          </div>
          <div className={pushStyles.previewBloc}>
            <p className={pushStyles.preview}>{getPreview(preview)}</p>
          </div>
          <div className={pushStyles.linkBloc}>
            <div style={{display: 'flex', alignItems: 'center'}}>
              <p className={pushStyles.more}>
                {t('event.guide.page.more.url')}
              </p>
              <div style={{marginLeft: 8}}>
                <IconContainer
                  classname={pushStyles.more}
                  color="BLACK"
                  icon="ARROW_RIGHT_NORMAL"
                  size={20}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </PolyLink>
  );
};

EventOrNewsList.propTypes = {
  campus: PropTypes.string,
  categories: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    label: PropTypes.string,
    color: PropTypes.string
  })),
  cover: PropTypes.shape({
    imageFile: PropTypes.object,
    name: PropTypes.string
  }),
  date: PropTypes.string,
  end_date: PropTypes.string,
  getCategories: PropTypes.func,
  href: PropTypes.string,
  isEvent: PropTypes.bool,
  live: PropTypes.bool,
  locale: PropTypes.object,
  objectID: PropTypes.string,
  preview: PropTypes.string,
  title: PropTypes.string
};

export default EventOrNewsList;
